import { useEffect, useRef, useState, type RefObject, type ReactNode } from 'react';
import styled from 'styled-components';
import type { Scene } from '@/three/shared';

/**
 * SceneCanvas - isolated leaf around the vanilla Three.js scenes.
 *
 * Owns: lazy-loading three + the chosen scene, reduced-motion / WebGL / low-power
 * fallbacks, pause-when-offscreen + pause-when-tab-hidden, resize, full disposal,
 * and per-device quality tiers. No framer-motion lives inside this tree.
 *
 *  - scene="atmosphere" → quiet stage haze (light shafts + bokeh).
 *  - scene="ribbonveil" → home hero: the welcome screen's silk ribbons restated
 *    as an additive glow on navy, mode="scroll" (camera dolly).
 *
 * (The welcome screen itself uses the React Three Fiber "Ribbon Symphony"
 * scene in components/animations/welcome, not this vanilla engine.)
 */

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`;

type SceneName = 'atmosphere' | 'ribbonveil';

interface SceneCanvasProps {
  scene: SceneName;
  mode: 'pointer' | 'scroll';
  scrollTarget?: RefObject<HTMLElement>;
  fallback?: ReactNode;
}

const loaders: Record<SceneName, () => Promise<new (c: HTMLCanvasElement, o: { quality: number; dprCap: number }) => Scene>> = {
  atmosphere: () => import('@/three/atmosphere').then((m) => m.Atmosphere),
  ribbonveil: () => import('@/three/ribbonVeil').then((m) => m.RibbonVeil),
};

function canRenderWebGL(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

export const SceneCanvas = ({ scene, mode, scrollTarget, fallback = null }: SceneCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!canRenderWebGL()) {
      setUseFallback(true);
      return;
    }

    let engine: Scene | null = null;
    let cancelled = false;
    let visible = true;
    let onScreen = false;

    const small = window.innerWidth < 768;
    const lowCore = (navigator.hardwareConcurrency ?? 8) <= 4;
    const quality = small ? 0.5 : lowCore ? 0.7 : 1;
    const dprCap = small ? 1.5 : 2;

    const pointerHandler = (e: PointerEvent) => {
      if (!engine) return;
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = -((e.clientY / window.innerHeight) * 2 - 1);
      engine.setPointer(nx, ny);
    };

    const progressProvider = () => {
      const el = scrollTarget?.current;
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      return -rect.top / window.innerHeight;
    };

    const syncRunning = () => {
      if (!engine) return;
      if (visible && onScreen) engine.start();
      else engine.stop();
    };

    const onVisibility = () => {
      visible = document.visibilityState !== 'hidden';
      syncRunning();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry?.isIntersecting ?? false;
        syncRunning();
      },
      { threshold: 0 },
    );

    const ro = new ResizeObserver(() => engine?.resize());

    loaders[scene]()
      .then((Ctor) => {
        if (cancelled || !canvasRef.current) return;
        engine = new Ctor(canvasRef.current, { quality, dprCap });
        if (mode === 'scroll') engine.progressProvider = progressProvider;
        // Pointer parallax is wanted in both modes (scroll scenes still sway).
        window.addEventListener('pointermove', pointerHandler, { passive: true });
        engine.resize();
        engine.renderOnce();
        document.addEventListener('visibilitychange', onVisibility);
        io.observe(canvasRef.current);
        ro.observe(canvasRef.current);
      })
      .catch(() => {
        if (!cancelled) setUseFallback(true);
      });

    return () => {
      cancelled = true;
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', pointerHandler);
      engine?.dispose();
      engine = null;
    };
  }, [scene, mode, scrollTarget]);

  if (useFallback) return <>{fallback}</>;
  return <Canvas ref={canvasRef} aria-hidden="true" />;
};
